<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="element-island m-auto mt-2.5 p-4">
        <h1 class="font-semibold text-3xl text-center">
          {{ $t('Guest.VariationOrderProvisionShow.title') }}
        </h1>

        <div
          v-if="variationOrder"
          class="flex flex-col space-y-4 mt-4 text-lg"
        >
          <div
            v-if="variationOrder.status === VariationOrderStatus.provisioned || variationOrder.status === VariationOrderStatus.approved"
            class="p-4 rounded-lg mx-auto"
            :style="{ backgroundColor: variationOrder.statusColour, color: variationOrder.statusTextColour }"
          >
            {{ $t('Guest.VariationOrderProvisionShow.approved') }}
          </div>

          <div>
            <span class="font-semibold">
              {{ $t('Guest.VariationOrderProvisionShow.variationDetails') }}
            </span>

            <p>
              {{ variationOrder.code }} - {{ variationOrder.description }}
            </p>
          </div>

          <div class="flex justify-between font-semibold">
            <span>
              {{ $t('Guest.VariationOrderProvisionShow.totalCost') }}
            </span>

            <span>
              {{ australianCurrency(variationOrder.totalBudget) }}
            </span>
          </div>

          <div v-if="variationOrder.trade_items?.length > 0">
            <span class="font-semibold">
              {{ $t('Guest.VariationOrderProvisionShow.costBreakdown') }}
            </span>

            <ul
              class="list-disc ml-10"
            >
              <li
                v-for="tradeItem in variationOrder.trade_items"
                :key="tradeItem.id"
              >
                <span>
                  {{ tradeItem.name }}
                </span>
                -
                <span>
                  {{ australianCurrency(tradeItem.budget) }}
                </span>
              </li>
            </ul>
          </div>


          <template v-if="variationOrder.status === VariationOrderStatus.priced">
            <div class="font-semibold text-lg text-red-600">
              {{ $t('Guest.VariationOrderProvisionShow.pricingDeclined') }}
            </div>
          </template>

          <template v-else>
            <button
              type="button"
              class="btn btn--primary-blue w-full disabled:cursor-not-allowed"
              :disabled="variationOrder.status === VariationOrderStatus.provisioned || variationOrder.status === VariationOrderStatus.approved"
              @click="provisionVariationOrder"
            >
              {{ $t('Guest.VariationOrderProvisionShow.approveVariationPricing') }}
            </button>

            <button
              type="button"
              class="btn btn--primary-blue w-full disabled:cursor-not-allowed"
              :disabled="variationOrder.status === VariationOrderStatus.provisioned || variationOrder.status === VariationOrderStatus.approved"
              @click="declineSubmissionVariationOrder"
            >
              {{ $t('Guest.VariationOrderProvisionShow.declineVariationPricing') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, parseUrlParams } from '@/helpers';
import { t } from '@/i18n';
import { VariationOrderStatus, VariationOrderStatusColours } from '@/models/VariationOrder';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed } from 'vue';

const props = defineProps<{
  variationId: string;
}>();

const signedParams = useSignedData();
const variationOrdersStore = useVariationOrdersStore();

variationOrdersStore.models.save(signedParams.models.variation_order);

const variationOrder = computed(() => {
  return variationOrdersStore.models.with('trade_items').find(parseInt(props.variationId));
});

const provisionVariationOrderAction = useStoreApiAction(variationOrdersStore.provisionVariationOrder);
const declineSubmissionVariationOrderAction = useStoreApiAction(variationOrdersStore.declineSubmissionVariationOrder);

const provisionVariationOrderParams = computed(() => {
  return parseUrlParams(signedParams.meta.provision_variation_url as string);
});

const declineSubmissionVariationOrderParams = computed(() => {
  return parseUrlParams(signedParams.meta.decline_submission_variation_url as string);
});

const provisionVariationOrder = () => {
  Swal.fire({
    icon: 'question',
    title: t('Guest.VariationOrderProvisionShow.approvePricingQuestion'),
    text: t('Guest.VariationOrderProvisionShow.approvePricingConfirmation'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      provisionVariationOrderAction.request(props.variationId, provisionVariationOrderParams.value).then(
        () => {
          Swal.fire({
            icon: 'success',
            title: t('Guest.VariationOrderProvisionShow.variationPricingApproved'),
            text: t('Guest.VariationOrderProvisionShow.closeWindowMessage'),
          });
        },
      ).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('Guest.VariationOrderProvisionShow.approvePricingFailed'),
          text: error?.data?.message,
        });
      });
    }
  });
};

const declineSubmissionVariationOrder = () => {
  Swal.fire({
    icon: 'question',
    title: t('Guest.VariationOrderProvisionShow.declinePricingQuestion'),
    showCancelButton: true,
    inputLabel: t('Guest.VariationOrderProvisionShow.reasonRequired'),
    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      declineSubmissionVariationOrderAction.request(props.variationId, declineSubmissionVariationOrderParams.value, {
        reason: result.value,
      }).then(
        () => {
          Swal.fire({
            icon: 'success',
            title: t('Guest.VariationOrderProvisionShow.variationPricingDeclined'),
            text: t('Guest.VariationOrderProvisionShow.closeWindowMessage'),
          });
        },
      ).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('Guest.VariationOrderProvisionShow.declinePricingFailed'),
          text: error?.data?.message,
        });
      });
    }
  });
};
</script>

<style scoped></style>
